/**
 * Incremental `LayoutStructure` + flatten.
 *
 * `buildLayoutStructure` is O(modules + edges): it indexes every module by
 * parent, routes every edge to its LCA space, and styles every aggregated edge.
 * Rebuilding it on every keystroke is the last per-edit O(N) floor. This module
 * keeps the structure between edits and PATCHES it from a module/edge identity
 * diff, so a single-module edit costs O(changed), not O(N).
 *
 * The serializer hands us identity-stable wire modules (unchanged modules keep
 * their object ref across edits). `IncrementalFlatten` propagates that stability
 * to the flattened `ModuleWithParent` clones the board consumes, and
 * `IncrementalLayoutStructure` keys all its diffing on those refs.
 *
 * Correctness model — two tiers:
 *   • ROUTING CHANGE (module added / removed / re-parented / renamed, or ANY
 *     edge change): topology can shift anywhere, so we fall back to a full
 *     `buildLayoutStructure`. These are the compiler's "structural" edits — rare.
 *   • ROUTING STABLE: the parent index, LCA routing, ports and internal calls
 *     are provably unchanged, so we reuse them. We only (a) swap the changed
 *     module refs into `byParent`/`roots` (so the engine renders fresh content)
 *     and (b) re-style the edges whose target interface lives on a module whose
 *     styling surface (interfaces / labels / fields / typeName) changed — found
 *     in O(changed) via an interface-owner → edge index. A pure value edit
 *     (description, a non-widget field) restyles nothing.
 *
 * Every patched output is byte-identical to a full rebuild — guarded by an
 * exhaustive equivalence fuzz (`incremental-structure.test.ts`).
 */
import type { DependencyEdge, Interface, Module } from "../arch-model/model.js";
import { moduleKey, stampQualifiedKeys, qualifiedKeyByModule } from "../arch-model/utils.js";
import {
  buildInterfaceIndex,
  buildLayoutStructure,
  makeRowAnchorForEdge,
  resolveWidgetFromDetails,
  routingHelpers,
  styleAggregatedEdge,
  type AggregatedEdge,
  type LayoutStructure,
  type ModuleWithParent,
} from "./layout.js";
import type { BoardEdge, Connection, ExternalPort } from "./types.js";

/**
 * Stateful flatten that preserves clone identity across edits. For each wire
 * module whose ref AND computed parent are unchanged since last call, the prior
 * `{...m, parentId}` clone is returned verbatim — so the structure's `byParent`
 * only has to swap the handful of clones that actually changed, instead of every
 * one (a fresh `{...m}` per module would churn all N refs each edit).
 */
export class IncrementalFlatten {
  // Persistent across edits. Keyed on wire-module identity, so stale entries
  // (removed modules) are simply garbage-collected — no pruning needed, and an
  // unchanged module is touched zero times (no re-set per edit).
  private readonly clone = new WeakMap<Module, ModuleWithParent>();
  private readonly parent = new WeakMap<Module, string | undefined>();

  flatten(roots: readonly Module[]): ModuleWithParent[] {
    // Ensure the source tree carries qualified keys; reused clones already have
    // them stamped, fresh clones get them copied below.
    stampQualifiedKeys(roots);
    const out: ModuleWithParent[] = [];

    const visit = (m: Module, parentId: string | undefined): void => {
      const cached = this.clone.get(m);
      let clone: ModuleWithParent;
      if (cached !== undefined && this.parent.get(m) === parentId) {
        clone = cached; // unchanged module + parent → stable identity, no writes
      } else {
        clone = { ...m, parentId } as ModuleWithParent;
        const qual = qualifiedKeyByModule.get(m);
        if (qual !== undefined) qualifiedKeyByModule.set(clone, qual);
        this.clone.set(m, clone);
        this.parent.set(m, parentId);
      }
      out.push(clone);
      const myKey = moduleKey(m);
      for (const c of m.children) visit(c, myKey);
    };
    for (const r of roots) visit(r, undefined);
    return out;
  }
}

/** The distinct interface-owner paths a styled edge depends on — i.e. the
 *  qualified path of each target interface, minus its final segment. A change to
 *  a module at one of these paths can alter this edge's widget / row-anchor. */
function edgeOwnerPaths(e: BoardEdge): string[] {
  const out = new Set<string>();
  for (const d of e.details) {
    if (!d.toInterfaceId) continue;
    const dot = d.toInterfaceId.lastIndexOf(".");
    if (dot < 0) continue;
    out.add(d.toInterfaceId.slice(0, dot));
  }
  return [...out];
}

/** Reconstruct the pre-styling `AggregatedEdge` from a built `BoardEdge`. The
 *  aspect colour (the only edge-level styling input) round-trips via the stored
 *  `widgetStyle.color`. */
function toAggregated(e: BoardEdge, parent: string | undefined): AggregatedEdge {
  return {
    parent,
    from: e.from,
    to: e.to,
    details: e.details,
    aspectColor: e.isAspect ? e.widgetStyle?.color : undefined,
  };
}

export class IncrementalLayoutStructure {
  private state:
    | {
        edges: readonly DependencyEdge[];
        byKey: Map<string, ModuleWithParent>;
        structure: LayoutStructure;
        // Auxiliary indexes (private; never exposed in the structure output).
        interfaceByPath: Map<string, Interface>;
        moduleById: Map<string, Module>;
        parentOf: Map<string, string | undefined>;
        qualPath: (id: string) => string;
        /** interface-owner path → the built edges that target an interface
         *  there (so a styling change restyles only the impacted edges). */
        ownerIndex: Map<string, BoardEdge[]>;
        /** built edge → the space (parent key) its array lives under. */
        edgeParent: Map<BoardEdge, string | undefined>;
      }
    | null = null;

  /** Compile the structure for this edit, reusing the prior one where the
   *  topology is provably unchanged. `flat` must come from an
   *  {@link IncrementalFlatten} so unchanged modules keep their refs. */
  update(flat: readonly ModuleWithParent[], edges: readonly DependencyEdge[]): LayoutStructure {
    const prev = this.state;
    if (!prev || prev.edges !== edges) return this.full(flat, edges);

    const nextByKey = new Map<string, ModuleWithParent>();
    for (const m of flat) nextByKey.set(moduleKey(m), m);

    if (nextByKey.size !== prev.byKey.size) return this.full(flat, edges);

    // Classify the edit. `changedRefs` = any clone whose ref differs (content
    // changed → needs a byParent swap). `stylingChanged` ⊆ changedRefs = those
    // whose edge-styling surface moved (→ restyle + interface-index patch).
    const changedRefs: ModuleWithParent[] = [];
    const stylingChanged: Array<{ key: string; old: ModuleWithParent; next: ModuleWithParent }> = [];
    for (const [k, m] of nextByKey) {
      const pm = prev.byKey.get(k);
      if (pm === undefined) return this.full(flat, edges); // add (with equal size ⇒ a swap)
      if (m === pm) continue; // identical clone — nothing changed
      if (pm.parentId !== m.parentId || pm.name !== m.name) return this.full(flat, edges); // routing change
      changedRefs.push(m);
      if (
        pm.interfaces !== m.interfaces ||
        pm.aspects !== m.aspects ||
        pm.fields !== m.fields ||
        pm.typeName !== m.typeName
      ) {
        stylingChanged.push({ key: k, old: pm, next: m });
      }
    }

    if (changedRefs.length === 0) {
      this.state = { ...prev, edges };
      return prev.structure;
    }

    // --- routing-stable patch -------------------------------------------------

    // 1. moduleById: point at the new refs (private index; mutate in place).
    for (const m of changedRefs) prev.moduleById.set(moduleKey(m), m);

    // 2. interfaceByPath: replace each styling-changed module's OWN interfaces.
    //    Descendants are separate flat entries — patched on their own iteration.
    for (const { key, old, next } of stylingChanged) {
      const qp = prev.qualPath(key);
      for (const i of old.interfaces) prev.interfaceByPath.delete(`${qp}.${i.name}`);
      for (const i of next.interfaces) prev.interfaceByPath.set(`${qp}.${i.name}`, i);
    }

    // 3. Re-style only the edges whose target interface lives on a changed module.
    let edgesByParent = prev.structure.edgesByParent;
    const affected = new Set<BoardEdge>();
    for (const { key } of stylingChanged) {
      for (const e of prev.ownerIndex.get(prev.qualPath(key)) ?? []) affected.add(e);
    }
    if (affected.size > 0) {
      const rowAnchor = makeRowAnchorForEdge(prev.qualPath, prev.moduleById);
      edgesByParent = new Map(prev.structure.edgesByParent);
      const parents = new Set<string | undefined>();
      for (const e of affected) parents.add(prev.edgeParent.get(e));
      for (const parent of parents) {
        const arr = prev.structure.edgesByParent.get(parent)!.map((e) => {
          if (!affected.has(e)) return e;
          const restyled = styleAggregatedEdge(toAggregated(e, parent), prev.interfaceByPath, rowAnchor);
          this.reindexEdge(prev, e, restyled, parent);
          return restyled;
        });
        edgesByParent.set(parent, arr);
      }
    }

    // 4. byParent + roots: swap changed clones (copy only touched arrays).
    const changedByKey = new Map(changedRefs.map((m) => [moduleKey(m), m] as const));
    const byParent = new Map(prev.structure.byParent);
    const touchedParents = new Set<string>();
    let rootTouched = false;
    for (const m of changedRefs) {
      if (m.parentId) touchedParents.add(m.parentId);
      else rootTouched = true;
    }
    for (const pk of touchedParents) {
      byParent.set(pk, prev.structure.byParent.get(pk)!.map((o) => changedByKey.get(moduleKey(o)) ?? o));
    }
    const roots = rootTouched
      ? prev.structure.roots.map((o) => changedByKey.get(moduleKey(o)) ?? o)
      : prev.structure.roots;

    // Ports + connections carry a widgetStyle derived from their TARGET
    // INTERFACE (P6b). That is NOT topology-stable — a `widget.*` interface edit
    // changes it while routing stays put — so re-derive them from the updated
    // interfaceByPath when styling changed, else a patched structure keeps the
    // OLD colour and diverges from a cold rebuild (invariant break).
    let externalPorts = prev.structure.externalPorts;
    let connections = prev.structure.connections;
    if (stylingChanged.length > 0) {
      const restyle = <T extends { details: BoardEdge["details"]; widgetStyle?: unknown; widgetTag?: unknown }>(o: T): T => {
        const w = resolveWidgetFromDetails(o.details, prev.interfaceByPath);
        const { widgetStyle: _s, widgetTag: _t, ...base } = o;
        return (w.style || w.tag
          ? { ...base, ...(w.style ? { widgetStyle: w.style } : {}), ...(w.tag ? { widgetTag: w.tag } : {}) }
          : base) as T;
      };
      const restylePorts = (m: Map<string, ExternalPort[]>) => {
        const out = new Map<string, ExternalPort[]>();
        for (const [k, ports] of m) out.set(k, ports.map(restyle));
        return out;
      };
      externalPorts = restylePorts(prev.structure.externalPorts);
      connections = new Map<string, Connection>();
      for (const [id, c] of prev.structure.connections) connections.set(id, restyle(c));
    }

    const structure: LayoutStructure = {
      byParent,
      edgesByParent,
      externalPorts,
      internalCalls: prev.structure.internalCalls,
      connections,
      roots,
    };

    this.state = {
      edges,
      byKey: nextByKey,
      structure,
      interfaceByPath: prev.interfaceByPath,
      moduleById: prev.moduleById,
      parentOf: prev.parentOf,
      qualPath: prev.qualPath,
      ownerIndex: prev.ownerIndex,
      edgeParent: prev.edgeParent,
    };
    return structure;
  }

  /** Swap a restyled edge into the private owner/parent indexes (paths and
   *  endpoints are unchanged by styling, so only the object ref moves). */
  private reindexEdge(
    s: NonNullable<IncrementalLayoutStructure["state"]>,
    oldEdge: BoardEdge,
    newEdge: BoardEdge,
    parent: string | undefined,
  ): void {
    s.edgeParent.delete(oldEdge);
    s.edgeParent.set(newEdge, parent);
    for (const owner of edgeOwnerPaths(newEdge)) {
      const arr = s.ownerIndex.get(owner);
      if (!arr) continue;
      const idx = arr.indexOf(oldEdge);
      if (idx >= 0) arr[idx] = newEdge;
    }
  }

  /** Cold path: full build + (re)build the private indexes. */
  private full(flat: readonly ModuleWithParent[], edges: readonly DependencyEdge[]): LayoutStructure {
    const structure = buildLayoutStructure(flat, edges);

    const byKey = new Map<string, ModuleWithParent>();
    const moduleById = new Map<string, Module>();
    const parentOf = new Map<string, string | undefined>();
    for (const m of flat) {
      const k = moduleKey(m);
      byKey.set(k, m);
      moduleById.set(k, m);
      parentOf.set(k, m.parentId);
    }
    const { qualPath } = routingHelpers(parentOf, flat);
    const interfaceByPath = buildInterfaceIndex(flat);

    const ownerIndex = new Map<string, BoardEdge[]>();
    const edgeParent = new Map<BoardEdge, string | undefined>();
    for (const [parent, arr] of structure.edgesByParent) {
      for (const e of arr) {
        edgeParent.set(e, parent);
        for (const owner of edgeOwnerPaths(e)) {
          const list = ownerIndex.get(owner);
          if (list) list.push(e);
          else ownerIndex.set(owner, [e]);
        }
      }
    }

    this.state = { edges, byKey, structure, interfaceByPath, moduleById, parentOf, qualPath, ownerIndex, edgeParent };
    return structure;
  }
}
